import {
  DISCOVERY_MODULE_ID,
  PITCH_MODULE_ID,
  OBJECTION_MODULE_ID,
  COLD_CALL_MODULE_ID,
  PLAY_MODULE_ID,
  ON_THE_FLY_MODULE_ID,
  MANAGEMENT_MODULE_ID,
  CONVERSATION_ANALYSIS_SETTINGS_MODULE_ID,
  DEAL_CLINIC_MODULE_ID,
} from "@/lib/modules/ids";
import {
  ROLE_PLAY_TYPE_MODULE_LABELS,
  toRolePlayType,
} from "@/lib/role-plays/scenario-types";

// The six modules that are role-play types read their name from the one
// scenario-type table (lib/role-plays/scenario-types.js); the three that are
// not a role-play type are named here.
export const MODULE_LABELS = {
  DISCOVERY: ROLE_PLAY_TYPE_MODULE_LABELS.DISCOVERY,
  PITCH: ROLE_PLAY_TYPE_MODULE_LABELS.PITCH,
  OBJECTIONS: ROLE_PLAY_TYPE_MODULE_LABELS.OBJECTIONS,
  COLD_CALL: ROLE_PLAY_TYPE_MODULE_LABELS.COLD_CALL,
  PLAYS: ROLE_PLAY_TYPE_MODULE_LABELS.PLAY,
  MANAGEMENT: ROLE_PLAY_TYPE_MODULE_LABELS.MANAGEMENT,
  ON_THE_FLY: "On-the-Fly Plays",
  CONVERSATION_ANALYSIS: "Conversation Analysis",
  DEAL_CLINIC: "Deal Clinic",
};

export const MODULE_IDS = {
  DISCOVERY: DISCOVERY_MODULE_ID,
  PITCH: PITCH_MODULE_ID,
  OBJECTIONS: OBJECTION_MODULE_ID,
  COLD_CALL: COLD_CALL_MODULE_ID,
  PLAYS: PLAY_MODULE_ID,
  ON_THE_FLY: ON_THE_FLY_MODULE_ID,
  // These synthetic IDs are the persisted moduleIds used by workspace module settings.
  MANAGEMENT: MANAGEMENT_MODULE_ID,
  CONVERSATION_ANALYSIS: CONVERSATION_ANALYSIS_SETTINGS_MODULE_ID,
  DEAL_CLINIC: DEAL_CLINIC_MODULE_ID,
};

export const MODULE_HREFS = {
  DISCOVERY: "/simulator/discovery",
  PITCH: "/simulator/pitch",
  OBJECTIONS: "/simulator/objections",
  COLD_CALL: "/simulator/cold-call",
  PLAYS: "/simulator/plays",
  ON_THE_FLY: "/simulator/on-the-fly",
  CONVERSATION_ANALYSIS: "/dashboard/conversation-analyzer",
};

const PRACTICE_TO_KEY = {
  ...Object.fromEntries(
    Object.entries(MODULE_HREFS).map(([key, href]) => [href, key]),
  ),
  "/simulator/management": "MANAGEMENT",
  "/dem-cog/reports": "DEAL_CLINIC",
};

const DASHBOARD_TO_KEY = {
  "/dashboard/discovery": "DISCOVERY",
  "/dashboard/pitch": "PITCH",
  "/dashboard/objections": "OBJECTIONS",
  "/dashboard/cold-call": "COLD_CALL",
  "/dashboard/plays": "PLAYS",
  "/dashboard/plays/on-the-fly": "ON_THE_FLY",
  "/dashboard/management": "MANAGEMENT",
  "/dashboard/transcript-results": "CONVERSATION_ANALYSIS",
  "/dashboard/conversation-analyzer": "CONVERSATION_ANALYSIS",
  "/simulator/on-the-fly": "ON_THE_FLY",
  "/dem-cog/reports": "DEAL_CLINIC",
};

const REPORT_TO_KEY = {
  "/workspaces/reports/discovery": "DISCOVERY",
  "/workspaces/reports/pitch": "PITCH",
  "/workspaces/reports/objections": "OBJECTIONS",
  "/workspaces/reports/cold-call": "COLD_CALL",
  "/workspaces/reports/plays": "PLAYS",
  "/workspaces/reports/management": "MANAGEMENT",
  "/workspaces/reports/transcript-results": "CONVERSATION_ANALYSIS",
  "/dem-cog/reports": "DEAL_CLINIC",
};

const ADMIN_TO_KEY = {
  "/workspaces/admin/data/discovery": "DISCOVERY",
  "/workspaces/admin/data/pitch": "PITCH",
  "/workspaces/admin/data/objections": "OBJECTIONS",
  "/workspaces/admin/data/cold-call": "COLD_CALL",
  "/workspaces/admin/data/plays": "PLAYS",
  "/workspaces/admin/data/management-scenarios": "MANAGEMENT",
  "/dem-cog/dems": "DEAL_CLINIC",
};

const PLAYBOOK_TO_KEY = {
  messaging: "PITCH",
  objections: "OBJECTIONS",
  discovery: "DISCOVERY",
  "cold-call": "COLD_CALL",
  plays: "PLAYS",
};

function normalize(value) {
  return typeof value === "string" ? value.trim() : "";
}

function getModuleTitle(module) {
  return normalize(module?.title) || normalize(module?.name);
}

function findModuleByKey(enabledModules, key, fallbackLabel) {
  const moduleId = MODULE_IDS[key];
  const moduleHref = MODULE_HREFS[key];
  return enabledModules.find((module) => {
    if (moduleId && module?.id === moduleId) return true;
    if (moduleHref && module?.href === moduleHref) return true;
    const defaultName = normalize(module?.name).toLowerCase();
    const defaultTitle = normalize(module?.title).toLowerCase();
    return Boolean(
      fallbackLabel &&
        (defaultName === fallbackLabel.toLowerCase() ||
          defaultTitle === fallbackLabel.toLowerCase())
    );
  });
}

export function getModuleLabelByKey(enabledModules = [], key, fallback) {
  if (!key) return fallback;
  const fallbackLabel = fallback || MODULE_LABELS[key] || "";
  const match = findModuleByKey(enabledModules, key, fallbackLabel);
  return getModuleTitle(match) || fallbackLabel;
}

export function isModuleEnabledByKey(enabledModules = [], key) {
  if (!key) return false;
  const moduleId = MODULE_IDS[key];
  const moduleHref = MODULE_HREFS[key];
  const canonicalLabel = MODULE_LABELS[key] || "";

  return enabledModules.some((module) => {
    if (moduleId && module?.id === moduleId) return true;
    if (moduleHref && module?.href === moduleHref) return true;
    // Legacy CONVERSATION_ANALYSIS records sometimes carry a `resolver` value
    // instead of the canonical name. Keep that path as a fallback.
    if (key === "CONVERSATION_ANALYSIS") {
      const resolver = normalize(module?.resolver).toLowerCase();
      if (["conversationanalysis", "conversation_analysis"].includes(resolver)) {
        return true;
      }
    }
    return Boolean(
      canonicalLabel &&
        normalize(module?.name).toLowerCase() === canonicalLabel.toLowerCase()
    );
  });
}

export function getModuleLabelForPracticeHref(enabledModules = [], href, fallback) {
  return getModuleLabelByKey(enabledModules, PRACTICE_TO_KEY[href], fallback);
}

export function getModuleLabelForDashboardHref(enabledModules = [], href, fallback) {
  return getModuleLabelByKey(enabledModules, DASHBOARD_TO_KEY[href], fallback);
}

export function getModuleLabelForReportHref(enabledModules = [], href, fallback) {
  return getModuleLabelByKey(enabledModules, REPORT_TO_KEY[href], fallback);
}

export function getModuleLabelForAdminHref(enabledModules = [], href, fallback) {
  return getModuleLabelByKey(enabledModules, ADMIN_TO_KEY[href], fallback);
}

export function getModuleLabelForPlaybookId(enabledModules = [], id, fallback) {
  return getModuleLabelByKey(enabledModules, PLAYBOOK_TO_KEY[id], fallback);
}

// Keep the missing default intentional: null means the module list could not
// be loaded, while [] means it loaded and no practice modules are enabled.
export function isPlaybookSectionVisible(enabledModules, id) {
  const key = PLAYBOOK_TO_KEY[id];
  return (
    !key ||
    !Array.isArray(enabledModules) ||
    isModuleEnabledByKey(enabledModules, key)
  );
}

// The module key space is not quite the type key space: the Plays module is
// keyed `PLAYS` where the RolePlayType enum says `PLAY`. Every other spelling
// a caller sends is resolved by the scenario-type table.
const ROLE_PLAY_TYPE_TO_MODULE_KEY = { PLAY: "PLAYS" };

function scenarioTypeModuleKey(type) {
  const rolePlayType = toRolePlayType(type);
  return rolePlayType
    ? ROLE_PLAY_TYPE_TO_MODULE_KEY[rolePlayType] || rolePlayType
    : normalize(type).toUpperCase();
}

export function getModuleLabelForScenarioType(enabledModules = [], type, fallback) {
  return getModuleLabelByKey(enabledModules, scenarioTypeModuleKey(type), fallback);
}

// The name a studio gave a scenario type's module, or null when it kept the
// default. A title like "Objection: Bicycle 5" keeps its short scenario label
// unless the studio renamed the module, so only a rename is returned here.
// Reads `customName` from fetchEnabledModulesForWorkspace.
export function getCustomModuleNameForScenarioType(enabledModules = [], type) {
  const key = scenarioTypeModuleKey(type);
  if (!key || !Array.isArray(enabledModules)) return null;
  const match = findModuleByKey(enabledModules, key, MODULE_LABELS[key] || "");
  return normalize(match?.customName) || null;
}
